import posthog, { type BootstrapConfig } from 'posthog-js'

import { APP_HOST, MARKETING_HOST } from '../config/config'

/**
 * PostHog cross-domain tracking.
 *
 * In production the marketing pages are served from `www.cai.ai` while the product
 * app lives on `app.cai.insure` — two different registrable domains, so PostHog's
 * first-party cookie cannot follow a visitor across the hop. Left alone, someone
 * who reads the marketing site and then starts a quote arrives as a second
 * anonymous person with an unrelated session replay, and the app host re-derives
 * their attribution from scratch (crediting `www.cai.ai` for traffic that actually
 * came from search or ads).
 *
 * The fix (https://posthog.com/tutorials/cross-domain-tracking) is a manual
 * handover: the departing page appends its PostHog identifiers to the outbound
 * URL's fragment, and the arriving page feeds them to `posthog.init` via
 * `bootstrap`. Subdomains of one domain need none of this — the cookie already
 * spans them — which is why this only kicks in between our two split-origin hosts.
 */

const DISTINCT_ID_PARAM = 'ph_distinct_id'
const SESSION_ID_PARAM = 'ph_session_id'
const IDENTIFIED_PARAM = 'ph_identified'
const HANDOVER_AT_PARAM = 'ph_ts'

const HANDOVER_PARAMS = [DISTINCT_ID_PARAM, SESSION_ID_PARAM, IDENTIFIED_PARAM, HANDOVER_AT_PARAM]

/**
 * How long a handed-over `sessionID` stays usable. PostHog idles a session out
 * after 30 minutes and rejects a bootstrapped id whose UUIDv7 timestamp is more
 * than 24 hours old — and a decorated link can be bookmarked or shared. Past this
 * window we keep the person (distinct id) and let PostHog start a fresh session.
 */
const SESSION_HANDOVER_TTL_MS = 30 * 60 * 1000

/**
 * Whether to force a person profile before handing off. We run
 * `person_profiles: 'identified_only'`, so an anonymous visitor has no person for
 * their first-touch `$initial_referrer` / `$initial_utm_*` to attach to; those
 * would be re-derived on the app host and credit the marketing site instead of the
 * real source. Creating the profile on the way out preserves the true attribution,
 * at the cost of billing that visitor's later events as identified. Flip to
 * `false` to keep the person/session stitch but give up first-touch attribution.
 */
const CREATE_PERSON_PROFILE_ON_HANDOFF: boolean = true

/**
 * Meta Pixel cross-domain click attribution.
 *
 * The pixel's `_fbc` cookie — the one that ties a conversion back to the ad that was
 * clicked — is first-party, so it dies at the same hop PostHog's does. Copying it
 * across isn't an option: `fbevents.js` is injected server-side into `<head>` (see
 * `backend/insure/middlewares/tracking_scripts_middleware.py`) and has already built
 * `_fbc` before any of this bundle runs.
 *
 * So we hand over the click id the way Meta documents instead — put `fbclid` back on
 * the outbound URL and let the destination's own pixel rebuild `_fbc` from it at init.
 * Nothing to coordinate on load order, and nothing to read on arrival.
 *
 * `_fbp` (the per-browser id) is deliberately not handed over: it has no URL
 * parameter, only the cookie the pixel writes for itself, so our two hosts keep
 * separate browser ids. That costs some match quality if we adopt the Conversions API
 * later — the click id is what ad attribution itself needs.
 */
const FBC_COOKIE = '_fbc'
const FBCLID_PARAM = 'fbclid'

/** `_fbc` is `fb.<subdomain-index>.<creation-time>.<click-id>`; the click id may itself contain dots. */
const FBC_PATTERN = /^fb\.\d+\.\d+\.(.+)$/

/**
 * Split a fragment into the parts we own and everything else. The app also uses
 * bare fragments for tab groups (`#documents`), so the parts we don't own are
 * carried through verbatim rather than round-tripped through `URLSearchParams`,
 * which would rewrite `#documents` as `#documents=`.
 */
function splitFragment(hash: string) {
  const parts = hash.replace(/^#/, '').split('&').filter(Boolean)
  const isOurs = (part: string) =>
    HANDOVER_PARAMS.some((name) => part === name || part.startsWith(`${name}=`))
  return {
    rest: parts.filter((part) => !isOurs(part)),
    handover: new URLSearchParams(parts.filter(isOurs).join('&')),
  }
}

function setOrDelete(params: URLSearchParams, name: string, value: string | undefined) {
  if (value === undefined) params.delete(name)
  else params.set(name, value)
}

/** True only for URLs pointing at the *other* one of our two split-origin hosts. */
function crossesOurHosts(target: URL): boolean {
  // Empty in dev/preview, where both surfaces share one origin and need no handover.
  if (!APP_HOST || !MARKETING_HOST) return false
  if (target.origin === window.location.origin) return false
  return target.hostname === APP_HOST || target.hostname === MARKETING_HOST
}

/** Read a cookie by name, tolerating a value that isn't valid percent-encoding. */
function readCookie(name: string): string | undefined {
  for (const part of document.cookie.split('; ')) {
    const separator = part.indexOf('=')
    if (separator < 1 || part.slice(0, separator) !== name) continue
    const value = part.slice(separator + 1)
    try {
      return decodeURIComponent(value)
    } catch {
      return value
    }
  }
  return undefined
}

/** Hand the PostHog person and session over in the fragment. Returns whether it did. */
function decoratePosthogIds(target: URL): boolean {
  if (!posthog.__loaded) return false

  const distinctId = posthog.get_distinct_id()
  if (!distinctId) return false

  if (CREATE_PERSON_PROFILE_ON_HANDOFF) posthog.createPersonProfile()

  // Every parameter is rewritten, not merely filled in: the click decorator mutates
  // an anchor's `href` in place, so a second click re-decorates an already-decorated
  // URL. If the visitor signed out in between, a left-behind `ph_identified` would
  // pair a fresh anonymous device id with `isIdentifiedID`, inventing a junk person.
  const { rest, handover } = splitFragment(target.hash)
  handover.set(DISTINCT_ID_PARAM, distinctId)
  handover.set(HANDOVER_AT_PARAM, String(Date.now()))
  setOrDelete(handover, IDENTIFIED_PARAM, posthog._isIdentified() ? '1' : undefined)
  setOrDelete(handover, SESSION_ID_PARAM, posthog.get_session_id() || undefined)

  target.hash = [...rest, handover.toString()].join('&')
  return true
}

/**
 * Put this visitor's Meta ad click id on the outbound URL, recovered from the `_fbc`
 * the pixel already built on this host. Returns whether it did — no-op for the many
 * visitors who never came from an ad, and wherever the pixel isn't running at all
 * (it is production-only). Overwrites rather than appends, since re-decorating an
 * already-decorated URL is normal here.
 */
function decorateMetaClickId(target: URL): boolean {
  const clickId = currentMetaClickId()
  if (!clickId || target.searchParams.get(FBCLID_PARAM) === clickId) return false

  target.searchParams.set(FBCLID_PARAM, clickId)
  return true
}

/**
 * This visitor's Meta ad click id, preferring the one on the current URL over the
 * `_fbc` cookie.
 *
 * `fbevents.js` is loaded async, so on a fresh ad landing there is a window where
 * `_fbc` has not been written yet. For a first-time visitor that window just loses
 * the click; for someone who clicked one of our ads before, it is worse — the cookie
 * still holds the *previous* click id until the pixel overwrites it, so trusting it
 * would credit the earlier campaign for this visit. The landing URL is authoritative
 * whenever it has an `fbclid` at all, and once it doesn't (any later page of the
 * visit) `_fbc` is both present and correct.
 */
function currentMetaClickId(): string | undefined {
  const fromLandingUrl = new URL(window.location.href).searchParams.get(FBCLID_PARAM)
  if (fromLandingUrl) return fromLandingUrl

  return readCookie(FBC_COOKIE)?.match(FBC_PATTERN)?.[1]
}

/** Query params carrying campaign attribution. Forwarded verbatim, no cookie fallback (see `decorateAttributionParams`). */
const ATTRIBUTION_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid']

/**
 * Forward this visitor's campaign attribution params onto the outbound URL, read
 * straight off the current landing URL. Unlike the Meta click id there's no cookie
 * to fall back to here, so this only fires when the CTA is clicked from the same
 * page the params landed on — a visitor who browses to another marketing page first
 * won't carry them on that specific event. That's an accepted gap: PostHog's
 * `$initial_utm_*` backfill (triggered by `createPersonProfile` above) already gives
 * person-level first-touch attribution regardless of which page the CTA was clicked
 * from, which is what actually answers "where did this person come from."
 */
function decorateAttributionParams(target: URL): boolean {
  const landingParams = new URL(window.location.href).searchParams
  let decorated = false
  for (const key of ATTRIBUTION_PARAMS) {
    const value = landingParams.get(key)
    if (value === null) continue
    target.searchParams.set(key, value)
    decorated = true
  }
  return decorated
}

/**
 * Decorate an outbound URL so the destination host can continue this visitor's
 * PostHog person and session, re-derive the Meta ad click that brought them, and
 * carry their UTM/gclid attribution across. Returns `url` untouched for same-origin
 * links, for hosts that aren't ours (e.g. `blog.cai.ai`), and when nothing has
 * anything to hand over — dev, preview, and prerender included.
 *
 * Side effect: may create the visitor's person profile, see
 * `CREATE_PERSON_PROFILE_ON_HANDOFF`.
 */
export function withCrossDomainIds(url: string): string {
  if (typeof window === 'undefined') return url

  let target: URL
  try {
    target = new URL(url, window.location.href)
  } catch {
    return url
  }
  if (!crossesOurHosts(target)) return url

  // All three always run — a visitor can lack any one of these trackers independently.
  const decorated = [
    decoratePosthogIds(target),
    decorateMetaClickId(target),
    decorateAttributionParams(target),
  ]
  if (!decorated.some(Boolean)) return url

  return target.toString()
}

/**
 * Whether the browser says this navigation came from our other host.
 *
 * The parameters are plain URL text, so anyone can mail a link that pins the
 * recipient's analytics identity to an id of their choosing. That buys an attacker
 * nothing but polluted analytics — a PostHog distinct id grants no access here,
 * authorization is the session's job — and a signature can't close it, because any
 * key a public bundle could sign with is a key an attacker can read out of that bundle.
 * The referrer is what's actually checkable, and it costs nothing: a genuine
 * handover always arrives from the other host, and `strict-origin-when-cross-origin`
 * sends that origin on every https hop.
 *
 * That policy is NOT free — it's `SECURE_REFERRER_POLICY` in `backend/insure/settings.py`,
 * set against Django's `same-origin` default. `same-origin` sends no referrer at all
 * cross-origin, which makes this check reject every real handover (it did exactly that
 * from ship until 2026-08-13, stitching zero sessions). Don't drop that setting.
 *
 * Fails closed. No referrer (typed URL, `rel=noreferrer`, a privacy extension) means
 * an unattributable id, so PostHog starts a fresh identity rather than trust it.
 */
function handoverCameFromOurOtherHost(): boolean {
  if (!APP_HOST || !MARKETING_HOST) return false
  try {
    const referrer = new URL(document.referrer)
    return (
      referrer.origin !== window.location.origin &&
      (referrer.hostname === APP_HOST || referrer.hostname === MARKETING_HOST)
    )
  } catch {
    return false
  }
}

/**
 * Consume the identifiers the other host handed over and turn them into a
 * `bootstrap` config for `posthog.init`. Must run *before* `posthog.init`.
 *
 * The parameters are stripped from the URL with `history.replaceState` on the way
 * through — forged ones included — so they never reach `$current_url`, the router's
 * fragment-driven tabs, or a link someone copies out of the address bar.
 */
export function readCrossDomainBootstrap(): BootstrapConfig | undefined {
  if (typeof window === 'undefined') return undefined

  const { rest, handover } = splitFragment(window.location.hash)
  if ([...handover.keys()].length === 0) return undefined

  const url = new URL(window.location.href)
  url.hash = rest.join('&')
  window.history.replaceState(window.history.state, '', url.toString())

  const distinctID = handover.get(DISTINCT_ID_PARAM)
  if (!distinctID || !handoverCameFromOurOtherHost()) return undefined

  const bootstrap: BootstrapConfig = { distinctID }
  if (handover.get(IDENTIFIED_PARAM) === '1') bootstrap.isIdentifiedID = true

  const sessionID = handover.get(SESSION_ID_PARAM)
  const handedOverAt = Number(handover.get(HANDOVER_AT_PARAM))
  if (sessionID && handedOverAt > 0 && Date.now() - handedOverAt < SESSION_HANDOVER_TTL_MS) {
    bootstrap.sessionID = sessionID
  }

  return bootstrap
}

/**
 * Decorate cross-host anchors when they're activated rather than when they're
 * rendered, and return a teardown.
 *
 * Render-time decoration doesn't survive: the marketing routes are prerendered and
 * React doesn't repair attribute mismatches while hydrating, so an `href` built
 * during render would keep its build-time (undecorated) value. Rewriting `href`
 * from a capture-phase listener also covers Cmd-click and middle-click, which open
 * the URL without a default action we could otherwise intercept.
 */
export function decorateCrossDomainLinks(): () => void {
  const onActivate = (event: Event) => {
    const anchor = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>('a[href]') : null
    if (!anchor) return
    const decorated = withCrossDomainIds(anchor.href)
    if (decorated !== anchor.href) anchor.href = decorated
  }

  document.addEventListener('click', onActivate, true)
  document.addEventListener('auxclick', onActivate, true)
  return () => {
    document.removeEventListener('click', onActivate, true)
    document.removeEventListener('auxclick', onActivate, true)
  }
}
